import { useEffect, useRef } from 'react';
import { api } from '@/lib/api';

const S3_ATTR_PATH = 'data-s3-path';
const S3_ATTR_EXPIRES = 'data-s3-expires';
const S3_CLASS = 's3-asset';
const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
const STALE_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Hook that polls the DOM every 30 minutes for S3-sourced elements
 * whose pre-signed URLs are about to expire, and refreshes them in place.
 */
export function useS3UrlRefresher(workspaceId: string | undefined) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!workspaceId) return;

    const refreshStaleUrls = async () => {
      const elements = document.querySelectorAll(`.${S3_CLASS}[${S3_ATTR_PATH}][${S3_ATTR_EXPIRES}]`);
      if (elements.length === 0) return;

      const now = Date.now();
      const stalePaths: string[] = [];
      const staleElements: Map<string, Element[]> = new Map();

      elements.forEach((el) => {
        const expires = parseInt(el.getAttribute(S3_ATTR_EXPIRES) || '0', 10);
        const path = el.getAttribute(S3_ATTR_PATH) || '';

        if (path && (now - expires >= STALE_THRESHOLD_MS || expires === 0)) {
          stalePaths.push(path);
          const existing = staleElements.get(path) || [];
          existing.push(el);
          staleElements.set(path, existing);
        }
      });

      if (stalePaths.length === 0) return;

      const uniquePaths = [...new Set(stalePaths)];

      try {
        const { data } = await api.post(`/api/v1/workspaces/${workspaceId}/signed-urls`, {
          paths: uniquePaths,
        });

        if (data?.data) {
          const urlMap: Record<string, string> = data.data;
          const newTimestamp = Date.now().toString();

          for (const [path, newUrl] of Object.entries(urlMap)) {
            const els = staleElements.get(path) || [];
            for (const el of els) {
              if (el.tagName === 'IMG') {
                (el as HTMLImageElement).src = newUrl;
              } else if (el.tagName === 'A') {
                (el as HTMLAnchorElement).href = newUrl;
              }
              el.setAttribute(S3_ATTR_EXPIRES, newTimestamp);
            }
          }
        }
      } catch (err) {
        console.error('[S3 URL Refresh] Failed to refresh signed URLs:', err);
      }
    };

    // Run once on mount (in case page was open a long time before this mounted)
    refreshStaleUrls();

    intervalRef.current = setInterval(refreshStaleUrls, REFRESH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [workspaceId]);
}

/**
 * Helper to generate S3 asset props for React elements.
 * Attach these to any <img> or <a> that renders an S3 pre-signed URL.
 */
export function s3AssetProps(path: string): Record<string, string> {
  return {
    className: S3_CLASS,
    [S3_ATTR_PATH]: path,
    [S3_ATTR_EXPIRES]: Date.now().toString(),
  };
}
